import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { BulkActionsBar, Skeleton, SparkIcon, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType, isGenerativeTaskLoose } from '@/shared/domain/taskTypes';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { useSpecificAIModels } from '@/features/model-setup/hooks/useSpecificAIModels';
import { usePlaygroundApi } from '@/features/playground/hooks/usePlaygroundApi';
import { useSummarizationInference } from '@/features/playground/hooks/useSummarizationInference';
import PlaygroundTopBar from './PlaygroundTopBar';
import ModelHeadersRow from './ModelHeadersRow';
import PlaygroundEmptyState from './PlaygroundEmptyState';
import PromptsSection from './PromptsSection';
import ExamplesToolbar from './ExamplesToolbar';
import DataRow from './DataRow';
import AddModelModal from './AddModelModal';
import PlaygroundConfirmModal from './PlaygroundConfirmModal';
import PlaygroundInspectModal from './PlaygroundInspectModal';
import SummarizationGpuControl from './SummarizationGpuControl';
import DownloadQueryFileButton from './DownloadQueryFileButton';
import {
    defaultPlaygroundSlots,
    dispatchPlaygroundSlots,
    estimateTokens,
    filterOutputKeysNotInBenchmark,
    isNoPromptSlot,
    orderSlotsByPromptNeed,
    playgroundOutputKey,
    shouldShowGpuControl,
    taskTeacherDisplayName,
    ALREADY_IN_BENCHMARK_TOAST,
    benchmarkSelectionBlockedReason,
    selectOneBenchmarkOutputPerExample,
    toggleBenchmarkOutputSelection,
    uniqueExampleCountFromOutputKeys,
} from './playgroundUtils';
import { parsedTextFor } from './OutputCell';
import '@/features/playground/styles/playground.css';

const MAX_SLOTS = 3;
const DEFAULT_EXAMPLE_COUNT = 5;

let slotCounter = 0;
const newSlotId = () => `slot-${Date.now()}-${slotCounter += 1}`;

let rowCounter = 0;
const newRowId = () => `row-${Date.now()}-${rowCounter += 1}`;

const emptyRow = () => ({
    id: newRowId(),
    value: '',
    recordId: null,
    response: null,
    benchmarkRequestExample: null,
});

const EXAMPLE_PLACEHOLDER = '{{example}}';

// The inference routes take a single prompt string; the design's SYSTEM +
// USER prompts are joined for dispatch (system first), and the `{{example}}`
// placeholder is guaranteed so dispatch never sends a half prompt.
const combinePromptForDispatch = ({ system, user } = {}) => {
    const text = [system, user]
        .map((p) => (p || '').toString().trim())
        .filter(Boolean)
        .join('\n\n');
    if (!text) return '';
    if (text.includes(EXAMPLE_PLACEHOLDER)) return text;
    return `${text}\n${EXAMPLE_PLACEHOLDER}`;
};

const isNerTaskType = (taskType) => {
    const tt = (taskType || '').toLowerCase();
    return (
        tt === TaskType.NER.toLowerCase() ||
        tt === TaskType.NER_WITH_RELATIONS.toLowerCase()
    );
};

const isClassificationTaskType = (taskType) =>
    (taskType || '').toLowerCase() === TaskType.classification.toLowerCase();

// Task-typed ``response`` payload for POST /playground/save-to-benchmark.
// Returns null when the output cannot produce a valid record (e.g. NER
// prediction without parsed entities).
const benchmarkResponseFor = (output, taskType) => {
    const parsed = output?.parsed;
    if (isNerTaskType(taskType)) {
        if (!Array.isArray(parsed?.entities)) return null;
        const payload = { entities: parsed.entities };
        if (Array.isArray(parsed?.relations)) payload.relations = parsed.relations;
        return payload;
    }
    if (isClassificationTaskType(taskType)) {
        const labels = Array.isArray(parsed)
            ? parsed.filter(Boolean)
            : Array.isArray(parsed?.labels)
                ? parsed.labels.filter(Boolean)
                : [];
        if (labels.length === 0) return null;
        return {
            labels: labels.map((l) => (typeof l === 'string' ? l : l?.label)).filter(Boolean),
        };
    }
    const text = parsedTextFor(output, taskType);
    if (!text) return null;
    return { response: text };
};

const SkeletonRow = ({ columns }) => (
    <div className="pg-row" data-testid="pg-skeleton-row">
        <div className="pg-row__body">
            {Array.from({ length: columns }, (_, i) => (
                <div className="pg-row__input-col" key={i}>
                    <Skeleton shapeType="paragraph" />
                </div>
            ))}
        </div>
    </div>
);

/**
 * Redesigned Playground workspace (Figma Pi5qDCdYvgmnu1GWjnwx3B): compares
 * up to three models side by side over a shared examples grid — top toolbar
 * (breadcrumb, Add Model, Run all), model column headers, shared / split
 * prompts, example rows with per-model output cells, and the add / shuffle /
 * clear / save-to-benchmark flows.
 *
 * Rendered both from the task page (full-screen overlay) and the standalone
 * `/playground` route. The usecase contract mirrors the previous Playground:
 * GlobalContext's current usecase, restored from `?usecaseId=` when absent.
 */
const PlaygroundPage = ({ onExit }) => {
    const location = useLocation();
    const {
        getCurrentUsecase,
        refreshUsecases,
        setCurrentUsecaseId,
    } = useContext(GlobalContext);

    // Read the context usecase on the first paint so summarization GPU
    // polling is enabled immediately. Waiting for an effect left
    // ``enabled`` false on remount (after model download) and skipped
    // the /status refresh that restores Stop GPU + TTL.
    const [localUsecase, setLocalUsecase] = useState(() => getCurrentUsecase());

    useEffect(() => {
        const contextUsecase = getCurrentUsecase();
        if (contextUsecase) setLocalUsecase(contextUsecase);
    }, [getCurrentUsecase]);

    useEffect(() => {
        if (!localUsecase) {
            const urlParams = new URLSearchParams(location.search);
            const usecaseId = urlParams.get('usecaseId');
            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
                refreshUsecases();
            }
        }
    }, [location, localUsecase, setCurrentUsecaseId, refreshUsecases]);

    const taskType = localUsecase?.task_type;
    const isGenerative = isGenerativeTaskLoose(taskType);

    const api = usePlaygroundApi();
    const { availableProviders } = useCommercialModels();
    const { specificAIModels, isLoading: isLoadingSpecificAI } = useSpecificAIModels(
        localUsecase?.client_id,
        localUsecase?._id,
        !!localUsecase
    );

    const displayNameFor = useCallback(
        (provider, model) => {
            if (!model) return '';
            if (provider === 'specificai') {
                const entry = specificAIModels.find((m) => m.value === model);
                return entry?.label || model;
            }
            return model;
        },
        [specificAIModels]
    );

    // ------------------------------------------------------------ state
    const [slots, setSlots] = useState([]);
    const [rows, setRows] = useState([emptyRow()]);
    const [outputs, setOutputs] = useState({});
    const [promptsCollapsed, setPromptsCollapsed] = useState(false);
    const [splitMode, setSplitMode] = useState(false);
    const [sharedPrompts, setSharedPrompts] = useState({ system: '', user: '' });
    const [slotPrompts, setSlotPrompts] = useState({});
    const [benchmarkMode, setBenchmarkMode] = useState(false);
    const [isSavingBenchmark, setIsSavingBenchmark] = useState(false);
    const [selectedOutputs, setSelectedOutputs] = useState(() => new Set());
    const [existingBenchmarkExamples, setExistingBenchmarkExamples] = useState(
        () => new Set()
    );
    const [isAddModelOpen, setIsAddModelOpen] = useState(false);
    const [confirm, setConfirm] = useState(null); // {type, ...payload}
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [runningSlots, setRunningSlots] = useState(() => new Set());
    const [isShuffling, setIsShuffling] = useState(false);
    const [isLoadingRows, setIsLoadingRows] = useState(true);
    const [inspect, setInspect] = useState(null); // {rowId, slotId}
    const [availableLabels, setAvailableLabels] = useState({
        label_names: [],
        entity_names: [],
        relation_names: [],
    });

    // Canonical label catalog (``get-labels``): powers stable entity/label
    // colors in NER highlights and classification tags.
    useEffect(() => {
        if (!localUsecase?._id) return;
        data_from_server_post('get-labels', localUsecase._id, (labels) => {
            setAvailableLabels(
                labels || { label_names: [], entity_names: [], relation_names: [] }
            );
        });
    }, [localUsecase?._id]);

    const allLabels = useMemo(
        () =>
            isNerTaskType(taskType)
                ? availableLabels.entity_names || []
                : availableLabels.label_names || [],
        [availableLabels, taskType]
    );

    // ------------------------------------------- per-usecase (re)seeding
    // Wait until the SpecificAI catalog fetch for this task has finished.
    // ``isLoading`` is false while the hook is still disabled (no usecase
    // yet); seeding then would lock in the teacher and skip V1.
    const seededForUsecaseRef = useRef(null);
    useEffect(() => {
        if (!localUsecase || isLoadingSpecificAI) return;
        if (seededForUsecaseRef.current === localUsecase._id) return;
        seededForUsecaseRef.current = localUsecase._id;

        const seeded = defaultPlaygroundSlots({
            specificAIModels,
            teacherModel: localUsecase.teacher_model,
            teacherProvider: localUsecase.teacher_provider,
            teacherDisplay: taskTeacherDisplayName(localUsecase),
        }).map((slot) => ({ ...slot, slotId: newSlotId() }));
        setSlots(orderSlotsByPromptNeed(seeded, isGenerative));
        setOutputs({});
        setSplitMode(false);
        setSlotPrompts({});
        setSharedPrompts({
            system: localUsecase.teacher_system_prompt || '',
            user: localUsecase.prompt || '',
        });
        setBenchmarkMode(false);
        setSelectedOutputs(new Set());
        setExistingBenchmarkExamples(new Set());
    }, [localUsecase, specificAIModels, isLoadingSpecificAI, isGenerative]);

    useEffect(() => {
        if (!localUsecase) return;
        setIsLoadingRows(true);
        api.fetchExamples(
            {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                first_index: 0,
                num_records: DEFAULT_EXAMPLE_COUNT,
            },
            (examples) => {
                const mapped = (examples || []).map((e) => ({
                    id: newRowId(),
                    value: e.value || '',
                    recordId: e.id,
                    response: e.response || null,
                    benchmarkRequestExample: e.is_benchmark ? e.value || '' : null,
                }));
                setRows(mapped.length > 0 ? mapped : [emptyRow()]);
                setOutputs({});
                setIsLoadingRows(false);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localUsecase?._id]);

    const rowIdsKey = rows.map((row) => row.id).join(',');
    useEffect(() => {
        if (!localUsecase) return;
        const texts = [...new Set(rows.map((row) => row.value).filter((text) => text.trim()))];
        if (texts.length === 0) return;
        data_from_server_post(
            'playground/benchmark-existing-examples',
            {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                request_examples: texts,
            },
            (response) => {
                setExistingBenchmarkExamples((prev) => {
                    const next = new Set(prev);
                    for (const text of response?.existing || []) next.add(text);
                    return next;
                });
            }
        );
        // Load / shuffle / add / remove change row ids. Do not refetch on
        // every keystroke in an example cell.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localUsecase?._id, rowIdsKey]);

    const knownBenchmarkExamples = useMemo(() => {
        const next = new Set(existingBenchmarkExamples);
        for (const row of rows) {
            if (row.benchmarkRequestExample) next.add(row.benchmarkRequestExample);
        }
        return next;
    }, [existingBenchmarkExamples, rows]);

    // -------------------------------------------------------- GPU session
    const summarizationInference = useSummarizationInference({
        clientId: localUsecase?.client_id,
        llmUsecaseId: localUsecase?._id,
        enabled: isGenerative,
    });
    const hasSpecificSlot = slots.some((s) => s.provider === 'specificai');
    // Start GPU is cluster-wide for generative Playground. Show it as soon
    // as the task type is known — do not wait for SpecificAI slots to
    // hydrate. An idle remount used to hide both the timer and the button
    // until /usecase-models seeded a slot.
    const showGpuControl = shouldShowGpuControl(isGenerative);
    const gpuBlocked = hasSpecificSlot && !summarizationInference.isRunning;

    // SpecificAI fine-tuned models on non-generative tasks run on raw input —
    // their prompt column shows the "no prompt needed" note (Figma
    // .Playground-Student-No-Prompt). Prompt-capable slots are kept contiguous
    // (first) so the shared panel / "Split prompts" spans every prompt model.
    const orderedSlots = useMemo(
        () => orderSlotsByPromptNeed(slots, isGenerative),
        [slots, isGenerative]
    );
    const noPromptSlotIds = useMemo(
        () =>
            new Set(
                orderedSlots
                    .filter((s) => isNoPromptSlot(s, isGenerative))
                    .map((s) => s.slotId)
            ),
        [orderedSlots, isGenerative]
    );

    // ---------------------------------------------------------- prompts
    const promptForSlot = useCallback(
        (slot) => (splitMode ? slotPrompts[slot.slotId] : sharedPrompts) || { system: '', user: '' },
        [splitMode, slotPrompts, sharedPrompts]
    );

    const handleSplitPrompts = useCallback(() => {
        setSlotPrompts(() => {
            const next = {};
            slots.forEach((slot) => {
                next[slot.slotId] = { ...sharedPrompts };
            });
            return next;
        });
        setSplitMode(true);
    }, [slots, sharedPrompts]);

    const applyUseForAll = useCallback(
        (slotId) => {
            const source = slotPrompts[slotId] || { system: '', user: '' };
            setSharedPrompts({ ...source });
            setSplitMode(false);
        },
        [slotPrompts]
    );

    const handleUseForAll = useCallback(
        (slotId) => {
            const promptOf = (id) => slotPrompts[id] || { system: '', user: '' };
            const source = promptOf(slotId);
            // Warn only when another model's prompt would actually be replaced
            // with something different (Figma 1631:120450).
            const replacesDifferentPrompt = slots.some((slot) => {
                if (slot.slotId === slotId || noPromptSlotIds.has(slot.slotId)) {
                    return false;
                }
                const other = promptOf(slot.slotId);
                return (
                    (other.system || '') !== (source.system || '') ||
                    (other.user || '') !== (source.user || '')
                );
            });
            if (replacesDifferentPrompt) {
                setConfirm({ type: 'use-for-all', slotId });
            } else {
                applyUseForAll(slotId);
            }
        },
        [slots, slotPrompts, noPromptSlotIds, applyUseForAll]
    );

    const confirmUseForAll = useCallback(() => {
        const { slotId } = confirm;
        setConfirm(null);
        applyUseForAll(slotId);
    }, [confirm, applyUseForAll]);

    const handleReplaceTaskPrompt = useCallback(
        (slotId) => {
            const prompts = slotId
                ? slotPrompts[slotId] || { system: '', user: '' }
                : sharedPrompts;
            const modelName = slotId
                ? slots.find((slot) => slot.slotId === slotId)?.displayName
                : null;
            setConfirm({ type: 'replace-prompt', prompts, modelName });
        },
        [slotPrompts, sharedPrompts, slots]
    );

    const confirmReplaceTaskPrompt = useCallback(() => {
        const { prompts } = confirm;
        setConfirm(null);
        api.saveTeacherAndPrompt(
            {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                prompt: prompts.user || '',
                teacher_system_prompt: prompts.system || null,
            },
            () => {
                sparkToast.success('Task prompt replaced');
                refreshUsecases?.();
            }
        );
    }, [confirm, api, localUsecase, refreshUsecases]);

    // ------------------------------------------------------------ models
    const handleAddModel = useCallback(
        ({ provider, model }) => {
            setIsAddModelOpen(false);
            if (!model) return;
            setSlots((prev) => {
                if (prev.length >= MAX_SLOTS) return prev;
                const slot = {
                    slotId: newSlotId(),
                    provider,
                    model,
                    displayName: displayNameFor(provider, model),
                };
                if (splitMode) {
                    setSlotPrompts((prevPrompts) => ({
                        ...prevPrompts,
                        [slot.slotId]: { ...sharedPrompts },
                    }));
                }
                return orderSlotsByPromptNeed([...prev, slot], isGenerative);
            });
        },
        [displayNameFor, splitMode, sharedPrompts, isGenerative]
    );

    const handleRemoveModel = useCallback((slot) => {
        setConfirm({ type: 'remove-model', slot });
    }, []);

    const confirmRemoveModel = useCallback(() => {
        const { slot } = confirm;
        setConfirm(null);
        setSlots((prev) => prev.filter((s) => s.slotId !== slot.slotId));
        setOutputs((prev) => {
            const next = {};
            for (const [rowId, bySlot] of Object.entries(prev)) {
                const { [slot.slotId]: _omit, ...rest } = bySlot;
                next[rowId] = rest;
            }
            return next;
        });
        setSlotPrompts((prev) => {
            const { [slot.slotId]: _omit, ...rest } = prev;
            return rest;
        });
        setSelectedOutputs((prev) => {
            const next = new Set(
                [...prev].filter((key) => !key.endsWith(`|${slot.slotId}`))
            );
            return next;
        });
        sparkToast.success(`${slot.displayName} removed`);
    }, [confirm]);

    const handleSwapModel = useCallback((slot) => {
        setConfirm({
            type: 'replace-model',
            slot,
            currentModelName: taskTeacherDisplayName(
                getCurrentUsecase() || localUsecase
            ),
        });
    }, [getCurrentUsecase, localUsecase]);

    const confirmReplaceModel = useCallback(() => {
        const { slot } = confirm;
        setConfirm(null);
        api.saveTeacherAndPrompt(
            {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                prompt: localUsecase.prompt || '',
                teacher: slot.model,
                provider: slot.provider,
                teacher_display: slot.displayName,
            },
            () => {
                sparkToast.success(`${slot.displayName} is now the task model`);
                refreshUsecases?.();
                setLocalUsecase((prev) =>
                    prev
                        ? {
                            ...prev,
                            teacher_model: slot.model,
                            teacher_provider: slot.provider,
                            teacher_model_display: slot.displayName,
                        }
                        : prev
                );
            }
        );
    }, [confirm, api, localUsecase, refreshUsecases]);

    // ------------------------------------------------------------- rows
    const handleAddExample = useCallback(() => {
        setRows((prev) => [emptyRow(), ...prev]);
    }, []);

    const handleInputChange = useCallback((rowId, value) => {
        setRows((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, value } : row))
        );
    }, []);

    const handleRemoveRow = useCallback((rowId) => {
        setRows((prev) => {
            const next = prev.filter((row) => row.id !== rowId);
            return next.length > 0 ? next : [emptyRow()];
        });
        setOutputs((prev) => {
            const { [rowId]: _omit, ...rest } = prev;
            return rest;
        });
        setSelectedOutputs((prev) => {
            return new Set([...prev].filter((key) => !key.startsWith(`${rowId}|`)));
        });
    }, []);

    const hasAnyExampleText = rows.some((row) => row.value.trim());
    const runDisabled = !hasAnyExampleText || slots.length === 0 || gpuBlocked;
    let runDisabledReason = null;
    if (gpuBlocked) {
        runDisabledReason = summarizationInference.isStarting
            ? 'Wait for the GPU to finish starting before running SpecificAI models.'
            : 'Start GPU before running SpecificAI models.';
    } else if (slots.length === 0) {
        runDisabledReason = 'Add a model to run examples.';
    } else if (!hasAnyExampleText) {
        runDisabledReason = 'Add at least one example to run.';
    }

    const handleShuffle = useCallback(() => {
        setConfirm({ type: 'shuffle' });
    }, []);

    const confirmShuffle = useCallback(() => {
        setConfirm(null);
        setIsShuffling(true);
        const params = {
            client_id: localUsecase.client_id,
            llm_usecase_id: localUsecase._id,
        };
        // One request per benchmark/dataset bucket: read the filtered count,
        // then pull a random window of DEFAULT_EXAMPLE_COUNT records.
        const pullRandom = (isBenchmark, onEmpty) => {
            data_from_server_post(
                'dataset-records',
                { ...params, is_benchmark: isBenchmark, first_index: 0, num_records: 1 },
                (probe) => {
                    const count = probe?.filtered_count || 0;
                    if (count === 0) {
                        onEmpty();
                        return;
                    }
                    const maxStart = Math.max(0, count - DEFAULT_EXAMPLE_COUNT);
                    const firstIndex = Math.floor(Math.random() * (maxStart + 1));
                    data_from_server_post(
                        'dataset-records',
                        {
                            ...params,
                            is_benchmark: isBenchmark,
                            first_index: firstIndex,
                            num_records: DEFAULT_EXAMPLE_COUNT,
                        },
                        (response) => {
                            const records = response?.records || [];
                            setRows(
                                records.length > 0
                                    ? records.map((r) => ({
                                        id: newRowId(),
                                        value: r.request_example || '',
                                        recordId: r._id,
                                        response: r.response || null,
                                        benchmarkRequestExample: isBenchmark
                                            ? r.request_example || ''
                                            : null,
                                    }))
                                    : [emptyRow()]
                            );
                            setOutputs({});
                            setIsShuffling(false);
                        }
                    );
                }
            );
        };
        pullRandom(true, () =>
            pullRandom(false, () => {
                setIsShuffling(false);
                sparkToast.warning('No dataset records available to shuffle');
            })
        );
    }, [localUsecase]);

    const handleClear = useCallback(() => {
        setConfirm({ type: 'clear' });
    }, []);

    const confirmClear = useCallback(() => {
        setConfirm(null);
        setRows([emptyRow()]);
        setOutputs({});
        setSelectedOutputs(new Set());
        setBenchmarkMode(false);
    }, []);

    // -------------------------------------------------------------- run
    const isRunning = runningSlots.size > 0;

    const handleRunAll = useCallback(() => {
        if (!localUsecase) return;
        if (gpuBlocked) {
            sparkToast.warning('Start the GPU session before running SpecificAI models');
            return;
        }
        const targets = rows.filter((row) => row.value.trim());
        if (targets.length === 0 || slots.length === 0) return;

        const rowIds = targets.map((r) => r.id);
        const examples = targets.map((r) => r.value);

        setOutputs((prev) => {
            const next = { ...prev };
            for (const rowId of rowIds) {
                next[rowId] = { ...(next[rowId] || {}) };
                for (const slot of slots) {
                    next[rowId][slot.slotId] = { status: 'loading' };
                }
            }
            return next;
        });
        setRunningSlots(new Set(slots.map((s) => s.slotId)));

        const runSlot = (slot) => {
            const prompt = combinePromptForDispatch(promptForSlot(slot));
            const isCommercial = slot.provider !== 'specificai';
            return api.inference({
                temperature: 0,
                top_p: 0.5,
                top_k: 1,
                max_tokens: 100,
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                examples,
                prompt,
                model_id: slot.model,
                isCommercialModel: isCommercial,
                provider: isCommercial ? slot.provider : undefined,
            })
                .then((response) => {
                    const results = response?.results || [];
                    setOutputs((prev) => {
                        const next = { ...prev };
                        rowIds.forEach((rowId, index) => {
                            const result = results[index] || {};
                            const rawText =
                                typeof result.raw_result === 'string'
                                    ? result.raw_result
                                    : result.raw_result != null
                                        ? JSON.stringify(result.raw_result, null, 2)
                                        : '';
                            const output = {
                                status: 'done',
                                rawText,
                                parsed: result.parsed_result,
                                // Only agentic runs return one; the cell shows
                                // the conversation instead of a parsed answer.
                                trajectory: result.trajectory,
                                view: 'parsed',
                            };
                            output.tokensOut = estimateTokens(
                                parsedTextFor(output, taskType) || rawText
                            );
                            next[rowId] = { ...(next[rowId] || {}), [slot.slotId]: output };
                        });
                        return next;
                    });
                })
                .catch((error) => {
                    if (!error?.alreadyNotified) {
                        sparkToast.error(error?.message || 'Failed to get inference results');
                    }
                    setOutputs((prev) => {
                        const next = { ...prev };
                        rowIds.forEach((rowId) => {
                            next[rowId] = {
                                ...(next[rowId] || {}),
                                [slot.slotId]: { status: 'error' },
                            };
                        });
                        return next;
                    });
                })
                .finally(() => {
                    setRunningSlots((prev) => {
                        const next = new Set(prev);
                        next.delete(slot.slotId);
                        return next;
                    });
                });
        };

        // Commercial models run concurrently; SpecificAI models run one at a
        // time so they don't thrash the shared in-pod GPU (a sleep/wake per
        // interleaved base). See dispatchPlaygroundSlots.
        dispatchPlaygroundSlots(slots, runSlot);
    }, [localUsecase, rows, slots, promptForSlot, api, gpuBlocked, taskType]);

    const handleToggleOutputView = useCallback((rowId, slotId) => {
        setOutputs((prev) => {
            const output = prev[rowId]?.[slotId];
            if (!output || output.status !== 'done') return prev;
            return {
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    [slotId]: {
                        ...output,
                        view: output.view === 'raw' ? 'parsed' : 'raw',
                    },
                },
            };
        });
    }, []);

    // After the parser is saved from the Inspect modal: refresh the local
    // usecase copy and re-parse every done commercial cell's raw output in
    // place (no new LLM calls) via /playground/parse-raw. Re-parsed cells
    // flip from the raw view to the parsed view so the result is visible.
    const handleParserSaved = useCallback((parserFunction) => {
        setLocalUsecase((prev) =>
            prev ? { ...prev, parser_function: parserFunction } : prev
        );
        refreshUsecases?.();
        sparkToast.success('Parser saved');

        const commercialSlotIds = slots
            .filter((slot) => slot.provider !== 'specificai')
            .map((slot) => slot.slotId);
        const targets = [];
        for (const [rowId, bySlot] of Object.entries(outputs)) {
            for (const slotId of commercialSlotIds) {
                const output = bySlot?.[slotId];
                if (output?.status === 'done') {
                    targets.push({ rowId, slotId, rawText: output.rawText || '' });
                }
            }
        }
        if (targets.length === 0 || !localUsecase) return;

        api.parseRaw(
            {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                raw_results: targets.map((target) => target.rawText),
            },
            (response) => {
                const results = response?.results || [];
                setOutputs((prev) => {
                    const next = { ...prev };
                    targets.forEach(({ rowId, slotId }, index) => {
                        const output = next[rowId]?.[slotId];
                        if (!output) return;
                        next[rowId] = {
                            ...next[rowId],
                            [slotId]: {
                                ...output,
                                parsed: results[index] ?? null,
                                view: 'parsed',
                            },
                        };
                    });
                    return next;
                });
            },
            (errorMessage) => {
                sparkToast.error(errorMessage || 'Failed to re-parse outputs');
            }
        );
    }, [slots, outputs, api, localUsecase, refreshUsecases]);

    // -------------------------------------------------------- benchmark
    const doneOutputKeys = useMemo(() => {
        const keys = [];
        for (const row of rows) {
            for (const slot of slots) {
                if (outputs[row.id]?.[slot.slotId]?.status === 'done') {
                    keys.push(playgroundOutputKey(row.id, slot.slotId));
                }
            }
        }
        return keys;
    }, [rows, slots, outputs]);

    const handleToggleBenchmarkMode = useCallback(() => {
        setBenchmarkMode((prev) => {
            if (prev) setSelectedOutputs(new Set());
            return !prev;
        });
    }, []);

    const handleToggleBenchmark = useCallback((rowId, slotId) => {
        const key = playgroundOutputKey(rowId, slotId);
        if (selectedOutputs.has(key)) {
            setSelectedOutputs((prev) => toggleBenchmarkOutputSelection(prev, rowId, slotId));
            return;
        }
        const text = rows.find((row) => row.id === rowId)?.value ?? '';
        const blockedReason = benchmarkSelectionBlockedReason(text, knownBenchmarkExamples);
        if (blockedReason) {
            sparkToast.error(blockedReason);
            return;
        }
        setSelectedOutputs((prev) => toggleBenchmarkOutputSelection(prev, rowId, slotId));
    }, [selectedOutputs, rows, knownBenchmarkExamples]);

    const exampleTextByRowId = useMemo(
        () => new Map(rows.map((row) => [row.id, row.value])),
        [rows]
    );

    const savableOutputKeys = useMemo(
        () => filterOutputKeysNotInBenchmark(
            doneOutputKeys,
            exampleTextByRowId,
            knownBenchmarkExamples
        ),
        [doneOutputKeys, exampleTextByRowId, knownBenchmarkExamples]
    );

    const selectableExampleCount = useMemo(
        () => uniqueExampleCountFromOutputKeys(doneOutputKeys),
        [doneOutputKeys]
    );

    const handleSelectAllBenchmark = useCallback(() => {
        const skipped = (
            uniqueExampleCountFromOutputKeys(doneOutputKeys)
            - uniqueExampleCountFromOutputKeys(savableOutputKeys)
        );
        if (skipped > 0) {
            sparkToast.error(
                skipped === 1
                    ? ALREADY_IN_BENCHMARK_TOAST
                    : `${skipped} examples already exist in the benchmark.`
            );
        }
        setSelectedOutputs(
            selectOneBenchmarkOutputPerExample(savableOutputKeys, selectedOutputs)
        );
    }, [doneOutputKeys, savableOutputKeys, selectedOutputs]);

    const handleSaveToBenchmark = useCallback(async () => {
        if (isSavingBenchmark) return;
        const rowById = Object.fromEntries(rows.map((r) => [r.id, r]));
        const slotById = Object.fromEntries(slots.map((s) => [s.slotId, s]));

        // The endpoint accepts one model_name per request — group by slot.
        const itemsBySlot = {};
        let invalid = 0;
        for (const key of selectedOutputs) {
            const [rowId, slotId] = key.split('|');
            const row = rowById[rowId];
            const output = outputs[rowId]?.[slotId];
            const response = benchmarkResponseFor(output, taskType);
            if (!row || !response) {
                invalid += 1;
                continue;
            }
            (itemsBySlot[slotId] ||= []).push({
                request_example: row.value,
                response,
            });
        }

        const groups = Object.entries(itemsBySlot);
        if (groups.length === 0) {
            sparkToast.warning('No valid outputs selected');
            return;
        }

        setIsSavingBenchmark(true);
        let saved = 0;
        let skipped = 0;
        try {
            for (const [slotId, items] of groups) {
                await new Promise((resolve, reject) => {
                    data_from_server_post(
                        'playground/save-to-benchmark',
                        {
                            client_id: localUsecase.client_id,
                            llm_usecase_id: localUsecase._id,
                            items,
                            model_name: slotById[slotId]?.displayName || null,
                        },
                        (response) => {
                            saved += response?.saved || 0;
                            skipped += response?.skipped || 0;
                            resolve();
                        },
                        (error) => reject(error)
                    );
                });
            }
            const parts = [`${saved} output${saved === 1 ? '' : 's'} saved to benchmark`];
            if (skipped > 0) parts.push(`${skipped} duplicate${skipped === 1 ? '' : 's'} skipped`);
            if (invalid > 0) parts.push(`${invalid} without a valid response skipped`);
            sparkToast.success(parts.join(' · '));
            setExistingBenchmarkExamples((prev) => {
                const next = new Set(prev);
                for (const key of selectedOutputs) {
                    const [rowId] = key.split('|');
                    const text = rowById[rowId]?.value;
                    if (text) next.add(text);
                }
                return next;
            });
            setBenchmarkMode(false);
            setSelectedOutputs(new Set());
        } catch (error) {
            sparkToast.error(error?.message || 'Failed to save outputs to benchmark');
        } finally {
            setIsSavingBenchmark(false);
        }
    }, [rows, slots, selectedOutputs, outputs, taskType, localUsecase, isSavingBenchmark]);

    // ------------------------------------------------------------ render
    // No model at all → the Figma 1806:104275 "Add Model" empty state replaces
    // the prompts / examples workspace (even when example rows exist). Only
    // shown once this usecase's slot seeding has run, so the workspace doesn't
    // flash while SpecificAI / teacher models are still loading.
    const isSeeded = !!localUsecase && seededForUsecaseRef.current === localUsecase._id;
    const showNoModelEmptyState = isSeeded && slots.length === 0;

    const projectName = localUsecase?.group_name;
    const taskName =
        (localUsecase?.sub_group_name && localUsecase.sub_group_name.trim()) ||
        localUsecase?.usecase_name;

    const inspectOutput = inspect
        ? outputs[inspect.rowId]?.[inspect.slotId]
        : null;

    const confirmModal = (() => {
        if (!confirm) return null;
        switch (confirm.type) {
            case 'remove-model':
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Remove model?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Remove model"
                        onConfirm={confirmRemoveModel}
                        onCancel={() => setConfirm(null)}
                    >
                        {`${confirm.slot.displayName} and its generated outputs will be removed from the comparison.`}
                    </PlaygroundConfirmModal>
                );
            case 'replace-model': {
                const currentModelName =
                    confirm.currentModelName ||
                    taskTeacherDisplayName(getCurrentUsecase() || localUsecase) ||
                    'the current model';
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Replace the task model?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Replace model"
                        primaryDisableWhenReadOnly
                        onConfirm={confirmReplaceModel}
                        onCancel={() => setConfirm(null)}
                    >
                        <div>
                            {`${taskName} will use ${confirm.slot.displayName} instead of ${currentModelName} to generate reference outputs. The task prompt stays as it is.`}
                        </div>
                        <div className="pg-confirm-modal__model-swap">
                            <span>{currentModelName}</span>
                            <SparkIcon
                                slug="arrow-right"
                                size="small"
                                className="pg-confirm-modal__model-swap-arrow"
                            />
                            <span>{confirm.slot.displayName}</span>
                        </div>
                    </PlaygroundConfirmModal>
                );
            }
            case 'replace-prompt': {
                const currentSystemPrompt = localUsecase?.teacher_system_prompt || '';
                const currentUserPrompt = localUsecase?.prompt || '';
                const promptSource = confirm.modelName
                    ? `${confirm.modelName}'s prompt`
                    : 'the prompt';
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Replace the task prompt?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Replace prompt"
                        primaryDisableWhenReadOnly
                        onConfirm={confirmReplaceTaskPrompt}
                        onCancel={() => setConfirm(null)}
                    >
                        <div>
                            {`${taskName} will start using ${promptSource} from the Playground - in training, evaluation, and production. Not just here.`}
                        </div>
                        {currentSystemPrompt && (
                            <div className="pg-confirm-modal__prompt-section">
                                <div className="pg-confirm-modal__prompt-label">
                                    Current system prompt - will be replaced:
                                </div>
                                <textarea
                                    className="pg-confirm-modal__preview"
                                    value={currentSystemPrompt}
                                    readOnly
                                />
                            </div>
                        )}
                        {currentUserPrompt && (
                            <div className="pg-confirm-modal__prompt-section">
                                <div className="pg-confirm-modal__prompt-label">
                                    Current user prompt - will be replaced:
                                </div>
                                <textarea
                                    className="pg-confirm-modal__preview"
                                    value={currentUserPrompt}
                                    readOnly
                                />
                            </div>
                        )}
                    </PlaygroundConfirmModal>
                );
            }
            case 'use-for-all':
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Use this prompt for all models?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Use for all"
                        onConfirm={confirmUseForAll}
                        onCancel={() => setConfirm(null)}
                    >
                        <span>
                            This System and User Prompt will become the shared
                            prompt, replacing the prompts of the other models.
                            <br />
                            This won&apos;t change the prompt saved to your task.
                        </span>
                    </PlaygroundConfirmModal>
                );
            case 'shuffle':
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Replace current examples?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Replace examples"
                        onConfirm={confirmShuffle}
                        onCancel={() => setConfirm(null)}
                    >
                        {`Your current examples and their outputs will be replaced with ${DEFAULT_EXAMPLE_COUNT} new ones from your dataset.`}
                    </PlaygroundConfirmModal>
                );
            case 'clear':
                return (
                    <PlaygroundConfirmModal
                        open
                        title="Clear all examples?"
                        feedbackType="warning"
                        className="pg-confirm-modal--warning"
                        primaryLabel="Clear all"
                        onConfirm={confirmClear}
                        onCancel={() => setConfirm(null)}
                    >
                        This will remove all examples and their outputs, and reset the
                        Playground to an empty state. Your models and prompts won&apos;t
                        change.
                    </PlaygroundConfirmModal>
                );
            default:
                return null;
        }
    })();

    return (
        // --pg-cols drives the shared grid template (input/label column +
        // one column per model) so headers, prompts and data cells align.
        <div className="pg-page" style={{ '--pg-cols': orderedSlots.length + 1 }}>
            <PlaygroundTopBar
                projectName={projectName}
                taskName={taskName}
                onExit={onExit}
                canAddModel={slots.length < MAX_SLOTS}
                onAddModel={() => setIsAddModelOpen(true)}
                onRunAll={handleRunAll}
                isRunning={isRunning}
                runDisabled={runDisabled}
                runDisabledReason={runDisabledReason}
                gpuControl={
                    showGpuControl ? (
                        <SummarizationGpuControl inference={summarizationInference} />
                    ) : null
                }
                queryFileControl={
                    isGenerative && localUsecase ? (
                        <DownloadQueryFileButton
                            clientId={localUsecase.client_id}
                            usecaseId={localUsecase._id}
                            taskName={taskName}
                            projectName={projectName}
                            taskType={taskType}
                        />
                    ) : null
                }
            />
            <div className="pg-scroll">
                <div className="pg-scroll__inner">
                    <ModelHeadersRow
                        slots={orderedSlots}
                        teacherModelId={localUsecase?.teacher_model}
                        onSwapModel={handleSwapModel}
                        onRemoveModel={handleRemoveModel}
                        onBack={onExit}
                    />
                    {showNoModelEmptyState ? (
                        <PlaygroundEmptyState
                            onAddModel={() => setIsAddModelOpen(true)}
                        />
                    ) : (
                        <>
                            <PromptsSection
                                collapsed={promptsCollapsed}
                                onToggleCollapsed={() => setPromptsCollapsed((v) => !v)}
                                slots={orderedSlots}
                                noPromptSlotIds={noPromptSlotIds}
                                splitMode={splitMode}
                                sharedPrompts={sharedPrompts}
                                onSharedPromptsChange={setSharedPrompts}
                                slotPrompts={slotPrompts}
                                onSlotPromptsChange={(slotId, next) =>
                                    setSlotPrompts((prev) => ({ ...prev, [slotId]: next }))
                                }
                                onSplitPrompts={handleSplitPrompts}
                                onUseForAll={handleUseForAll}
                                onReplaceTaskPrompt={handleReplaceTaskPrompt}
                            />
                            <ExamplesToolbar
                                onAddExample={handleAddExample}
                                onShuffle={handleShuffle}
                                onClear={handleClear}
                                onSaveToBenchmark={handleToggleBenchmarkMode}
                                onRunAll={handleRunAll}
                                isRunning={isRunning}
                                runDisabled={runDisabled}
                                runDisabledReason={runDisabledReason}
                                clearDisabled={!hasAnyExampleText}
                                shuffleDisabled={isShuffling}
                                benchmarkDisabled={doneOutputKeys.length === 0}
                                benchmarkActive={benchmarkMode}
                            />
                            <div className="pg-rows">
                                {isLoadingRows || isShuffling
                                    ? Array.from({ length: DEFAULT_EXAMPLE_COUNT }, (_, i) => (
                                        <SkeletonRow key={i} columns={orderedSlots.length + 1} />
                                    ))
                                    : rows.map((row) => (
                                        <DataRow
                                            key={row.id}
                                            row={row}
                                            slots={orderedSlots}
                                            outputs={outputs[row.id]}
                                            taskType={taskType}
                                            allLabels={allLabels}
                                            expanded={row.id === expandedRowId}
                                            onInputChange={handleInputChange}
                                            onRemoveRow={handleRemoveRow}
                                            onExpandRow={(rowId) =>
                                                setExpandedRowId((prev) =>
                                                    prev === rowId ? null : rowId
                                                )
                                            }
                                            onToggleOutputView={handleToggleOutputView}
                                            onInspectLogic={(rowId, slotId) =>
                                                setInspect({ rowId, slotId })
                                            }
                                            benchmarkMode={benchmarkMode}
                                            selectedOutputs={selectedOutputs}
                                            onToggleBenchmark={handleToggleBenchmark}
                                        />
                                    ))}
                            </div>
                            {benchmarkMode && (
                                <div className="pg-benchmark-bar fs-unmask">
                                    <BulkActionsBar
                                        selectedCount={selectedOutputs.size}
                                        totalCount={selectableExampleCount}
                                        onSelectAll={handleSelectAllBenchmark}
                                        onDeselectAll={() => setSelectedOutputs(new Set())}
                                        actions={[
                                            {
                                                label: 'Save to benchmark',
                                                onClick: handleSaveToBenchmark,
                                                disabled: selectedOutputs.size === 0,
                                                loading: isSavingBenchmark,
                                                disableWhenReadOnly: true,
                                            },
                                            {
                                                label: 'Cancel',
                                                onClick: handleToggleBenchmarkMode,
                                            },
                                        ]}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <AddModelModal
                open={isAddModelOpen}
                onClose={() => setIsAddModelOpen(false)}
                onAdd={handleAddModel}
                clientId={localUsecase?.client_id}
                llmUsecaseId={localUsecase?._id}
                taskType={taskType}
                existingModels={slots.map((slot) => slot.model)}
            />
            {confirmModal}
            <PlaygroundInspectModal
                open={!!inspect}
                usecase={localUsecase}
                rawText={inspectOutput?.rawText || ''}
                onClose={() => setInspect(null)}
                onSaved={handleParserSaved}
            />
        </div>
    );
};

export default PlaygroundPage;
